import { useState, useRef, useCallback, useEffect } from 'react';
import type { DockSide, DockTarget } from '../store/useDockLayoutStore';
import { useDockLayoutStore } from '../store/useDockLayoutStore';
import {
    resolveDockTarget,
    setLiveDockEntryRects,
    clearLiveDockEntryRects,
    type ResolvedDockTarget,
} from '../dock/dockSnapResolver';
import { collectDockEntryRects } from '../dock/dockStripRects';
import { dockOverlayNode } from '../dock/dockOverlay';

export type PanelPosition = 'left' | 'right' | 'top' | 'bottom';
export type SnapTarget = PanelPosition | null;
export interface UsePanelDragOptions {
    panelRef: React.RefObject<HTMLElement>;
    initialPanelPosition: PanelPosition;
    /** Instance id used for self-exclusion in relative dock splitting */
    panelId?: string;
    onSnap?: (snapTarget: PanelPosition) => void;
    /** Dock-layout aware drop: receives the full side+index decision.
        When provided it replaces onSnap entirely. */
    onDockTarget?: (target: DockTarget) => void;
    onFloat?: (e: MouseEvent) => void;
    getSnapZone?: (x: number, y: number) => SnapTarget;
    isMinimized?: boolean;
}
export interface UsePanelDragResult {
    isDraggingPanel: boolean;
    setIsDraggingPanel: React.Dispatch<React.SetStateAction<boolean>>;
    dragOffset: { x: number; y: number } | null;
    setDragOffset: React.Dispatch<
        React.SetStateAction<{ x: number; y: number } | null>
    >;
    dragPanelSize: { width: number; height: number } | null;
    setDragPanelSize: React.Dispatch<
        React.SetStateAction<{ width: number; height: number } | null>
    >;
    dragPos: { x: number; y: number } | null;
    setDragPos: React.Dispatch<
        React.SetStateAction<{ x: number; y: number } | null>
    >;
    snapTarget: SnapTarget;
    setSnapTarget: React.Dispatch<React.SetStateAction<SnapTarget>>;
    handlePanelDragStart: (
        e: React.MouseEvent,
        opts?: { isMinimized?: boolean },
    ) => void;
    snapOverlay: React.ReactNode;
}
export function usePanelDrag({
    panelRef,
    initialPanelPosition,
    panelId,
    onSnap,
    onDockTarget,
    onFloat,
    getSnapZone: getSnapZoneProp,
    isMinimized,
}: UsePanelDragOptions): UsePanelDragResult {
    const [isDraggingPanel, setIsDraggingPanel] = useState(false);
    const [dragOffset, setDragOffset] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [dragPanelSize, setDragPanelSize] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(
        null,
    );
    const [snapTarget, setSnapTarget] = useState<SnapTarget>(null);
    /** Exact insert-band geometry for entry-relative hits (null = edge band) */
    const [snapOverlayRect, setSnapOverlayRect] = useState<
        ResolvedDockTarget['overlayRect']
    >(null);
    const [panelPosition, setPanelPosition] =
        useState<PanelPosition>(initialPanelPosition);
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
        null,
    );
    const currentSnapTargetRef = useRef<SnapTarget>(null);
    useEffect(() => {
        currentSnapTargetRef.current = snapTarget;
    }, [snapTarget]);
    /** Strip side per entry id from the shared dock layout store */
    const getEntrySide = useCallback(
        (entryId: string): DockSide | undefined => {
            const { strips } = useDockLayoutStore.getState();
            return (Object.keys(strips) as DockSide[]).find((s) =>
                strips[s].includes(entryId),
            );
        },
        [],
    );
    const getSnapZone = useCallback(
        (x: number, y: number): SnapTarget => {
            if (getSnapZoneProp) return getSnapZoneProp(x, y);
            const margin = 80;
            const w = window.innerWidth;
            const h = window.innerHeight;
            if (x < margin) return 'left';
            if (x > w - margin) return 'right';
            if (y < margin) return 'top';
            if (y > h - margin) return 'bottom';
            return null;
        },
        [getSnapZoneProp],
    );
    const setPanelPositionStable = useCallback((pos: PanelPosition) => {
        setPanelPosition(pos);
    }, []);
    const handlePanelDragStart = useCallback(
        (e: React.MouseEvent, opts?: { isMinimized?: boolean }) => {
            void opts; // kept for signature compatibility; drag start is identical minimized or not
            if (!panelRef.current) return;
            const rect = panelRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
            setDragStart({ x: e.clientX, y: e.clientY });
            setDragPos({ x: e.clientX, y: e.clientY });
            setDragPanelSize({ width: rect.width, height: rect.height });
            setIsDraggingPanel(true);
            setSnapTarget(null);
        },
        [panelRef],
    );
    useEffect(() => {
        if (!isDraggingPanel) return;
        let dragEnded = false;
        const handleMouseMove = (e: MouseEvent) => {
            setDragPos({ x: e.clientX, y: e.clientY });
            // Shared dock resolution: outermost edge bands append to that
            // edge's strip; hovering a docked entry — or the band beside it —
            // targets an insert between entries (dockSnapResolver owns the
            // geometry and reports the exact highlight rect)
            setLiveDockEntryRects(
                collectDockEntryRects().rects,
                getEntrySide,
            );
            const target = resolveDockTarget(e.clientX, e.clientY, panelId);
            if (target.side !== currentSnapTargetRef.current) {
                setSnapTarget(target.side);
                currentSnapTargetRef.current = target.side;
            }
            setSnapOverlayRect(target.overlayRect);
        };
        const handleMouseUp = (e: MouseEvent) => {
            if (dragEnded) return;
            dragEnded = true;
            setLiveDockEntryRects(
                collectDockEntryRects().rects,
                getEntrySide,
            );
            const target = resolveDockTarget(e.clientX, e.clientY, panelId);
            setIsDraggingPanel(false);
            setDragOffset(null);
            setDragPanelSize(null);
            if (target.side) {
                if (onDockTarget) {
                    onDockTarget({ side: target.side, index: target.index });
                } else if (
                    onSnap &&
                    target.side !== panelPosition &&
                    target.index === 0
                ) {
                    // Legacy edge-only consumers keep their behavior
                    setPanelPositionStable(target.side);
                    setTimeout(() => onSnap(target.side!), 10);
                }
            } else if (onFloat) {
                onFloat(e);
            }
            setSnapTarget(null);
            setSnapOverlayRect(null);
            setDragStart(null);
            setDragPos(null);
            currentSnapTargetRef.current = null;
            clearLiveDockEntryRects();
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
        return undefined;
    }, [
        isDraggingPanel,
        panelPosition,
        panelId,
        onSnap,
        onDockTarget,
        onFloat,
        getSnapZone,
        getEntrySide,
        setPanelPositionStable,
    ]);
    const snapOverlay =
        isDraggingPanel && snapTarget
            ? dockOverlayNode({ side: snapTarget, overlayRect: snapOverlayRect })
            : null;
    return {
        isDraggingPanel,
        setIsDraggingPanel,
        dragOffset,
        setDragOffset,
        dragPanelSize,
        setDragPanelSize,
        dragPos,
        setDragPos,
        snapTarget,
        setSnapTarget,
        handlePanelDragStart,
        snapOverlay,
    };
}
